
import { ContactRole, Contact, ContactUser } from "@/store/user-management/types";

// Vuex/Vue
import { Component, Vue, Prop, Watch} from "vue-property-decorator";
import { namespace, Getter, Action, State, Mutation } from "vuex-class";

// Sv Components
import NavHeader from "@/components/NavHeader.vue";
import AreaSelect from "@/components/form/AreaSelect.vue";
import ContactRoles from "@/views/ContactRoles.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";

// Third Party Components
import VuePerfectScrollbar from "vue-perfect-scrollbar";

// Modals
import ContactsModal from "@/components/contacts/ContactsModal.vue";
import DeleteContactModal from "@/components/contacts/DeleteContactModal.vue";

// Third Party
import { get } from "lodash";

// Types
import { UserPermissions, FeaturesList, VueBootstrapField } from "@/store/types";

// Namespaces
const UserManagement = namespace("userManagement");
const Areas = namespace("areas");


@Component({
	components: {
		"nav-header": NavHeader,
		"contact-roles": ContactRoles,
		"area-select": AreaSelect,
		"sureview-icon": SureViewIcon,
		"contacts-modal": ContactsModal,
		"delete-contact-modal": DeleteContactModal,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
	}
})
export default class ContactsPage extends Vue {
	// -- Vuex -- //
	@Mutation private setIsLoading: (loading: boolean) => void;

	@State featuresList: FeaturesList;
	@State (state => state.User.userPermissions) permissions: UserPermissions;

	@UserManagement.Action getContacts: ({ page, pageSize, filter}: { page: number, pageSize: number, filter: string }) => Promise<number>;
	@UserManagement.Action loadContactRoleList: () => Promise<void>;
	@UserManagement.State contacts: ContactUser[];


	@Areas.Getter getAreaTitle: (groupId: number) => string;

	@Areas.State protected areaDictionary: { [groupId: number]: string };
	@Areas.Action protected fetchAreaDictionary: (regionUrl?: string | null) => Promise<void>;

	// -- Properties -- //

	private filterSearch: string = "";

	private contactsBusy: boolean = true;

	private contactsModal: boolean = false;

	private deleteContactsModal: boolean = false;

	private showContactRoleModal: boolean = false;

	private pageCount: number = 30;

	private pageIndex: number = 1;

	private selectedContactRole: ContactUser = null;

	private filteredContacts: ContactUser[] = [];

    private fields: VueBootstrapField[] = [
		{ key: 'name', label: 'Full Name', sortable: true },
		{ key: 'groupID', label: 'Area', sortable: true,
			formatter: (groupId, key, item) => groupId > 0 ? this.getAreaById(groupId) : ""
		},
		{ key: 'email', label: 'Email', sortable: true },
		{ key: 'telephone', label: 'Telephone', sortable: true },
		{ key: 'contactRoles', label: 'Role', sortable: true },
		{ key: 'contactRoleGroups', label: 'Role Areas', sortable: true },
		{ key: 'actions', label: 'Actions', sortable: false }
	];

	// -- Computed Properties -- //

	/**
	 * @summary computed property if contacts feature is enabled
	 * @returns boolean
	 */
	private get isContactsEnabled(): boolean {
		return get(this.featuresList, ["Contacts"]);
	}

	private get initTableBusy(): boolean {
		return this.contactsBusy && this.contacts.length == 0;
	}

	private get getSelectedUserContact(): ContactUser {
		// NOTE: Issue with backend implementation, returns an
		// array which should be a single object
		const contactUser = { ...this.selectedContactRole };
		if (Array.isArray(contactUser.contactRoles)) {
			contactUser.contactRoles = contactUser.contactRoles[0];
		}
		return contactUser;
	}

	// If ManageContactRoles permissions is on / off
	public get isManagmentPermissionsEnabled(): boolean {
		return (this.permissions.isAdmin || this.permissions.canManageContactRoles);
	}

	// -- Watchers -- // 

	@Watch("filterSearch")
	private async setFilterOptions(newFilter: string, oldFilter: string) {
		if (newFilter.length > 0) {
			this.pageIndex = 1;
		} else {
			this.pageIndex = Math.floor(this.contacts.length / this.pageCount);
		}
		await this.getContactsList();
	}

	// -- Vue Lifecycle Hooks -- //

	private async mounted(): Promise<void> {
		await this.getContacts({ page: this.pageIndex, pageSize: this.pageCount, filter: "" });
		await this.loadContactRoleList();
		await this.fetchAreaDictionary();
		this.contactsBusy = false;
	}

	// -- Methods -- //

	private getAreaById(groupId): string {
		// This needs to exist due to formatter within vue bootstrap table
		// using vuex getter will be outside the 'this' scope
		return this.getAreaTitle(groupId);
	}

	/**
	 * @summary Method to set the current contact and enable update contact modals visibility state to true.
	 */
	private setSelectedContact(contact: ContactUser): void {
		this.setIsLoading(true);
		this.selectedContactRole = contact;
		this.contactsModal = true;
	}

	/**
	 * @summary Method to set the current contact and enable delete contact modals visibility state to true.
	 */
	private setContactForDeletion(contact: ContactUser): void {
		this.selectedContactRole = contact;
		this.deleteContactsModal = true;
	}

	/**
	 * @summary Method to deal with y axis, triggers when at end of scrollbar
	 */
	private async psHandlerReachEnd(event: any): Promise<void> {
		if (this.contactsBusy) return;
		if (this.filterSearch.length > 0 && this.filteredContacts.length < this.pageCount) return;

		// First check if we got any content in our list.
		if (!this.contacts || this.contacts.length == 0) return;
		if ((this.contacts.length / this.pageCount) < this.pageIndex) return;
		this.pageIndex++;

		await this.getContactsList();
	}

	private async getContactsList(): Promise<void> {
		this.contactsBusy = true;
		await this.getContacts({ page: this.pageIndex, pageSize: this.pageCount, filter: this.filterSearch });
		this.contactsBusy = false;
	}

	private displayAddNewContactModal(): void {
		this.setIsLoading(true);
		this.contactsModal = !this.contactsModal
	}
}
